import { ref, reactive, computed } from 'vue';
import { useChargesStore } from '@/services/UserService/useChargesStore';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import { useEntryProcessState } from '@/composables/useEntryProcessState';
import { pluralizeEntryWithCount } from '@/services/utils';
import ChargeService from '@/services/ChargeService';
const LARGE_SELECTION_THRESHOLD = 100;

/**
 * Composable for fixed fee bulk operations shared between the
 * Time Entries page (`EntryList.vue`) and the Fixed Fee Time
 * Entries tab (`FixedFeeTimeEntriesTab.vue`).
 *
 * Owns:
 *  - confirmation message generation
 *  - `bulkConvertEntries` handler (make billable / non-billable)
 *  - `confirmMakeBillable` / `confirmMakeNonBillable` dialog flows
 *  - Assign to Fixed Fee modal state + API handler
 *  - `bulkChangeMatter` handler (move entries to a different matter)
 *  - change matter dialog state (`showChangeMatterDialog`, `changeMatterForm`)
 *  - `bulkOperationLoading` state
 *  - confirmation dialog refs (`billableConfirmRef`, `nonBillableConfirmRef`)
 *  - full `useEntryProcessState` API (pass-through)
 *
 * @param {Object} options
 * @param {() => string[]} options.getEntryIds
 *   Returns the array of entry IDs currently selected for bulk actions.
 * @param {() => void} options.clearSelection
 *   Clears the selection state after a successful operation.
 * @param {() => Promise<void>} [options.onAfterOperation]
 *   Called after a successful bulk operation (e.g. reload entries, emit events).
 * @param {'this' | 'their'} [options.messageContext='their']
 *   Controls wording: "this fixed fee" (specific fee page) or
 *   "their fixed fee" (entry list page where entries may belong to different fees).
 * @param {boolean} [options.showLargeSelectionWarning=false]
 *   When true, appends a warning when selection exceeds LARGE_SELECTION_THRESHOLD.
 * @param {((billingType: string, convertedCount: number) => void) | null} [options.onTrackConvert=null]
 *   Optional tracking callback fired after a successful convert.
 * @param {(() => string | null) | null} [options.getMatterId=null]
 *   Returns the matter ID for loading available charges (assign-to-fee).
 *   Required for assign-to-fee functionality.
 * @param {(() => string | null) | null} [options.getExcludeChargeId=null]
 *   Returns a charge ID to exclude from the available charges list.
 *   Useful on FixedFeeTimeEntriesTab to exclude the current charge during reassign.
 * @param {((successCount: number, targetName: string, chargeId: string) => void) | null} [options.onTrackAssign=null]
 *   Optional tracking callback fired after a successful assign-to-fee.
 * @param {((successCount: number, targetMatterId: string) => void) | null} [options.onTrackChangeMatter=null]
 *   Optional tracking callback fired after a successful change matter operation.
 *
 * @returns {Object} Bulk operation API for the consuming component.
 *
 * @example
 * // In EntryList.vue
 * const {
 *   bulkOperationLoading,
 *   billableConfirmRef,
 *   nonBillableConfirmRef,
 *   billableConfirmMessage,
 *   nonBillableConfirmMessage,
 *   confirmMakeBillable,
 *   confirmMakeNonBillable,
 *   showAssignToFeeModal,
 *   availableCharges,
 *   loadingCharges,
 *   openAssignToFeeModal,
 *   closeAssignToFeeModal,
 *   handleAssignToFee,
 *   showChangeMatterDialog,
 *   changeMatterForm,
 *   openChangeMatterDialog,
 *   closeChangeMatterDialog,
 *   confirmChangeMatter,
 *   entryProcessStateMap,
 * } = useFixedFeeBulkOperations({
 *   getEntryIds: () => selectedEntryObjects.value.map((e) => e.id),
 *   clearSelection: () => { selectedEntryIds.value = []; },
 *   onAfterOperation: () => emit('loadEntries'),
 *   messageContext: 'their',
 *   showLargeSelectionWarning: true,
 *   getMatterId: () => selectedEntryObjects.value[0]?.matter_id || null,
 * });
 */
export function useFixedFeeBulkOperations({
  getEntryIds,
  clearSelection,
  onAfterOperation = null,
  messageContext = 'their',
  showLargeSelectionWarning = false,
  onTrackConvert = null,
  getMatterId = null,
  getExcludeChargeId = null,
  onTrackAssign = null,
  onTrackChangeMatter = null
}) {
  // ─── Dependencies ────────────────────────────────────────
  const chargesStore = useChargesStore();
  const {
    handleWarning,
    handleSuccess,
    handleError
  } = useSnackbarHandler();

  // Pass-through the full entry process state API so consumers don't need
  // to import it separately for assign-to-fee, move-to-matter, etc.
  const {
    stateMap: entryProcessStateMap,
    setProcessing: setEntriesProcessing,
    clear: clearEntryProcessState,
    handleResult: handleBulkOperationResult,
    setAllFailed,
    getState: getEntryProcessState,
    getRowClass
  } = useEntryProcessState();

  // ─── Loading State ───────────────────────────────────────
  const bulkOperationLoading = ref(false);

  // ─── Confirmation Dialog Refs ────────────────────────────
  const billableConfirmRef = ref(null);
  const nonBillableConfirmRef = ref(null);

  // ─── Confirmation Messages ──────────────────────────────
  const billableConfirmMessage = computed(() => {
    const count = getEntryIds().length;
    const entryText = `${count} ${count === 1 ? 'entry' : 'entries'}`;
    const base = `Are you sure you want to make ${entryText} billable?\n\nThis will remove them from ${messageContext} fixed fee and make them billable hourly. They will appear on the next invoice as standard hourly entries.`;
    if (showLargeSelectionWarning && count > LARGE_SELECTION_THRESHOLD) {
      return `${base}\n\n You are converting a large number of entries (${count}). This may take a moment to complete.`;
    }
    return base;
  });
  const nonBillableConfirmMessage = computed(() => {
    const count = getEntryIds().length;
    const entryText = `${count} ${count === 1 ? 'entry' : 'entries'}`;
    const base = `Are you sure you want to make ${entryText} non-billable?\n\nThis will remove them from ${messageContext} fixed fee and mark them as non-billable. These entries will not appear on invoices.`;
    if (showLargeSelectionWarning && count > LARGE_SELECTION_THRESHOLD) {
      return `${base}\n\n You are converting a large number of entries (${count}). This may take a moment to complete.`;
    }
    return base;
  });

  // ─── Bulk Convert Handler ──────────────────────────────
  const bulkConvertEntries = async billingType => {
    const entryIds = getEntryIds();
    const count = entryIds.length;
    const typeLabel = billingType.toLowerCase();
    bulkOperationLoading.value = true;

    // Clear previous states and mark all selected as processing
    clearEntryProcessState();
    setEntriesProcessing(entryIds);
    try {
      const result = await chargesStore.bulkConvertFromFixedFee(entryIds, billingType);

      // Handle result — marks inline error icons on failed entries
      const hasErrors = handleBulkOperationResult(entryIds, result.failures, 'entryId', 'error');
      const entryText = pluralizeEntryWithCount(count);
      if (hasErrors) {
        handleWarning(`Converted ${result.convertedCount} of ${entryText} ${typeLabel}. See warning icons for failed entries.`);
      } else {
        handleSuccess(`Successfully made ${entryText} ${typeLabel}`);
      }

      // Fire tracking callback if provided
      if (onTrackConvert && result.convertedCount > 0) {
        onTrackConvert(billingType, result.convertedCount);
      }
      clearSelection();
      await onAfterOperation?.();
    } catch (err) {
      // Mark all entries as failed on general error
      setAllFailed(entryIds);
      handleError(`Failed to make entries ${typeLabel}`);
      console.error(`Error making entries ${typeLabel}:`, err);
    } finally {
      bulkOperationLoading.value = false;
    }
  };

  // ─── Confirmation Dialog Handlers ──────────────────────
  const confirmMakeBillable = async () => {
    const confirmed = await billableConfirmRef.value?.open();
    if (confirmed) {
      await bulkConvertEntries('Billable');
    }
  };
  const confirmMakeNonBillable = async () => {
    const confirmed = await nonBillableConfirmRef.value?.open();
    if (confirmed) {
      await bulkConvertEntries('NonBillable');
    }
  };

  // ─── Assign to Fixed Fee ──────────────────────────────
  const showAssignToFeeModal = ref(false);
  const availableCharges = ref([]);
  const loadingCharges = ref(false);

  /**
   * Load available fixed fee charges for the given matter.
   * Optionally excludes a specific charge (e.g. the current charge on reassign).
   */
  const loadAvailableCharges = async matterId => {
    if (!matterId) return;
    loadingCharges.value = true;
    try {
      // acceptsTime, not billed=false: this feeds bulkAssignEntriesToCharge,
      // which now accepts a Billed + Time Unlocked fee too (FFLOCK T-08).
      // acceptsTime subsumes billed, so it's left undefined here rather than
      // passing a stale `false` that reads as if it still mattered.
      const charges = await ChargeService.listCharges(matterId, undefined, true);
      const excludeId = getExcludeChargeId?.();
      availableCharges.value = excludeId ? charges.filter(c => c.id !== excludeId) : charges;
    } catch (err) {
      handleError('Failed to load available fees');
    } finally {
      loadingCharges.value = false;
    }
  };

  /**
   * Open the assign-to-fee modal immediately, then load charges in background.
   * The modal shows a loading state on the select while charges fetch.
   */
  const openAssignToFeeModal = () => {
    const matterId = getMatterId?.();
    if (!matterId) {
      handleError('Could not determine the matter for selected entries');
      return;
    }
    availableCharges.value = [];
    showAssignToFeeModal.value = true;
    loadAvailableCharges(matterId);
  };
  const closeAssignToFeeModal = () => {
    showAssignToFeeModal.value = false;
  };

  /**
   * Handle confirm from LlAssignToFixedFeeModal.
   * Calls ChargeService.bulkAssignEntriesToCharge and processes results.
   *
   * @param {Object|string} selectedFixedFee - Selected charge object or ID
   */
  const handleAssignToFee = async selectedFixedFee => {
    const chargeId = selectedFixedFee?.id || selectedFixedFee;
    const entryIds = getEntryIds();
    if (!chargeId || !entryIds.length) return;
    showAssignToFeeModal.value = false;
    const count = entryIds.length;
    bulkOperationLoading.value = true;
    clearEntryProcessState();
    setEntriesProcessing(entryIds);
    try {
      const targetCharge = availableCharges.value.find(c => c.id === chargeId);
      const targetName = targetCharge?.description || targetCharge?.label || 'selected fee';
      const result = await ChargeService.bulkAssignEntriesToCharge(chargeId, entryIds, true);

      // Handle result — marks inline error icons on failed entries
      const hasErrors = handleBulkOperationResult(entryIds, result.errors, 'itemId', 'reason');
      const entryText = pluralizeEntryWithCount(count);
      if (hasErrors) {
        handleWarning(`Assigned ${result.successCount} of ${entryText} to ${targetName}. See warning icons for failed entries.`);
      } else {
        handleSuccess(`Successfully assigned ${entryText} to ${targetName}`);
      }

      // Fire tracking callback if provided
      if (onTrackAssign && result.successCount > 0) {
        onTrackAssign(result.successCount, targetName, chargeId);
      }
      clearSelection();
      await onAfterOperation?.();
    } catch (err) {
      setAllFailed(entryIds);
      handleError('Failed to assign entries to fee');
      console.error('Error assigning entries to fee:', err);
    } finally {
      bulkOperationLoading.value = false;
    }
  };

  // ─── Change Matter Dialog State ──────────────────────────
  const showChangeMatterDialog = ref(false);
  const changeMatterForm = reactive({
    client: null,
    matter: null
  });
  const openChangeMatterDialog = () => {
    changeMatterForm.client = null;
    changeMatterForm.matter = null;
    showChangeMatterDialog.value = true;
  };
  const closeChangeMatterDialog = () => {
    showChangeMatterDialog.value = false;
    changeMatterForm.client = null;
    changeMatterForm.matter = null;
  };

  /**
   * No-op handler for the @matterChange event from ChangeMatterDialog.
   * Can be extended for validation in the future.
   */
  const handleChangeMatterMatterChange = () => {};

  // ─── Change Matter Confirm Handler ───────────────────────
  const confirmChangeMatter = async () => {
    const targetMatterId = changeMatterForm.matter?.id;
    if (!targetMatterId) return;
    const entryIds = getEntryIds();
    const count = entryIds.length;
    if (!count) return;
    const targetMatterName = changeMatterForm.matter.label || changeMatterForm.matter.name;
    bulkOperationLoading.value = true;

    // Clear previous states and mark all selected as processing
    clearEntryProcessState();
    setEntriesProcessing(entryIds);
    try {
      // Close the dialog first (consistent with other bulk operations)
      closeChangeMatterDialog();
      const result = await chargesStore.bulkMoveFixedFeeEntriesToMatter(entryIds, targetMatterId);

      // Handle result — marks inline error icons on failed entries
      const hasErrors = handleBulkOperationResult(entryIds, result.errors, 'itemId', 'reason');
      const entryText = pluralizeEntryWithCount(count);
      if (hasErrors) {
        handleWarning(`Moved ${result.successCount} of ${entryText} to ${targetMatterName}. See warning icons for failed entries.`);
      } else {
        handleSuccess(`Successfully moved ${entryText} to ${targetMatterName}`);
      }

      // Fire tracking callback if provided
      if (onTrackChangeMatter && result.successCount > 0) {
        onTrackChangeMatter(result.successCount, targetMatterId);
      }
      clearSelection();
      await onAfterOperation?.();
    } catch (err) {
      // Mark all entries as failed on general error
      setAllFailed(entryIds);
      handleError('Failed to move entries to matter');
      console.error('Error moving entries to matter:', err);
    } finally {
      bulkOperationLoading.value = false;
    }
  };
  return {
    // Loading state
    bulkOperationLoading,
    // Confirmation dialog refs (bind to <LlConfirmationDialog ref="...">)
    billableConfirmRef,
    nonBillableConfirmRef,
    // Confirmation messages (bind to :message prop)
    billableConfirmMessage,
    nonBillableConfirmMessage,
    // Handlers (bind to toolbar @mark-billable / @mark-non-billable)
    confirmMakeBillable,
    confirmMakeNonBillable,
    // Lower-level convert (if consumer needs to skip confirmation)
    bulkConvertEntries,
    // Assign to Fixed Fee
    showAssignToFeeModal,
    availableCharges,
    loadingCharges,
    loadAvailableCharges,
    openAssignToFeeModal,
    closeAssignToFeeModal,
    handleAssignToFee,
    // Change Matter dialog state + handlers
    showChangeMatterDialog,
    changeMatterForm,
    openChangeMatterDialog,
    closeChangeMatterDialog,
    handleChangeMatterMatterChange,
    confirmChangeMatter,
    // Entry process state API (pass-through for assign-to-fee, move-to-matter, etc.)
    entryProcessStateMap,
    setEntriesProcessing,
    clearEntryProcessState,
    handleBulkOperationResult,
    setAllFailed,
    getEntryProcessState,
    getRowClass
  };
}